import {BaseElementAsync, BASIS, OSkinableInit, PickInit} from "back/commons/basis";
import {REG} from 'lib/commons/registry';
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {BaseAreaView, BaseAreaViewAsync, IView, IViewContainer, IViewsContainer, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {Area, IArea, IAreaPointer} from "lib/commons/areas";
import {ILastDatasBuilder, JLastDatas, LASTDATAS, OLastDatasInit} from "lib/commons/lastDatas";
import {Resizer} from "back/commons/widgets/resizer";


/** Config d'un c-blocks-collapsable. */
export interface OBlocksCollapsableInit<C> extends OSkinableInit, OLastDatasInit, PickInit<BlocksCollapsable<C>, 'areas' | 'areasContext'> {
	openBlocks?: string[]
	lastDatas?: JLDBlocksCollapsable
}

export interface JLDBlocksCollapsable extends JLastDatas {
	/** Liste des codes de block ouverts. Si présent, les blocs non cités sont clos */
	open?: string[]
}

export class BlocksCollapsable<C = {}> extends BaseElementAsync implements IViewsContainer, ILastDatasBuilder {
	protected _areas?: IArea<C>[];
	protected _areasContext?: C;
	protected _params: OBlocksCollapsableInit<C>;

	get areasContext(): C {return this._areasContext}

	set areasContext(val: C) {
		this._areasContext = val;
		this.refresh();
	}

	get areas(): IArea<C>[] {return this._areas}

	async openBlock(block: CollapsableBlockArea<C>, lastDatas?: JLastDatas): Promise<boolean> {
		await block.show(lastDatas);
		//this._blocksCtn.scrollToChild(block);
		return true;
	}

	get selectedBlocks(): CollapsableBlockArea<C>[] {
		let collapsableBlockAreas = Array.from(this._blocksCtn.childNodes).filter((node) => IS_CollapsableBlockArea(node) && node.opened) as CollapsableBlockArea<C>[];
		return collapsableBlockAreas;
	}

	async addArea(area: IArea<C>, options?: { open?: boolean, position?: number }, initBlock?: OCollapsableBlockInit) {
		if (this._areas) this._areas.push(area);
		else this._areas = [area];
		let before: Element = null;
		if (options && (typeof options.position === 'number')) {
			let collapsableBlockAreas = Array.from(this._blocksCtn.childNodes).filter((node) => IS_CollapsableBlockArea(node) && node.opened) as CollapsableBlockArea<C>[];
			before = collapsableBlockAreas[options.position - 1];
			let p = options.position;
			while (before && p-- > 0) before = before.nextElementSibling;
		}
		const block = this._newBlock(area, before as CollapsableBlockArea<C>, initBlock);
		if ((options && options.open) || (this._params && this._params.lastDatas && this._params.lastDatas.open && this._params.lastDatas.open.includes(area.getId())))
			await this.openBlock(block);
		this._refreshUi();
	}

	async closeBlock(block: CollapsableBlockArea<C>, silently?: boolean, removeFromAreas?: boolean): Promise<boolean> {
		if (!await block.closeBlockView(silently)) return false;
		if (removeFromAreas && block.area && this._areas) {
			const idx = this._areas.indexOf(block.area);
			if (idx >= 0) this._areas.splice(idx, 1);
		}
		block.remove();
		return true;
	}

	getBlockByCode(code: string): CollapsableBlockArea<C> {
		if (code != null) for (let block = this._blocksCtn.firstElementChild as CollapsableBlockArea<C>; block; block = block.nextElementSibling as CollapsableBlockArea<C>) if (block.code === code) return block;
		return null;
	}

	hasBlocks(): boolean {return this._blocksCtn.firstElementChild != null}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (options && options.visible) {
			let selectedBlocks = this.selectedBlocks;
			if (selectedBlocks)
				for (const block of selectedBlocks) {
					const r = block.visitViews(visitor, options);
					if (r !== undefined) return r;
				}
		} else {
			let collapsableBlockAreas = Array.from(this._blocksCtn.childNodes).filter((node) => IS_CollapsableBlockArea(node) && node.opened) as CollapsableBlockArea<C>[];
			for (const block of collapsableBlockAreas) {
				const r = block.visitViews(visitor, options);
				if (r !== undefined) return r;
			}
		}
	}

	async visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (options && options.visible) {
			let selectedBlocks = this.selectedBlocks;
			if (selectedBlocks)
				for (const block of selectedBlocks) {
					const r = await block.visitViewsAsync(visitor, options);
					if (r !== undefined) return r;
				}
		} else {
			let collapsableBlockAreas = Array.from(this._blocksCtn.childNodes).filter((node) => IS_CollapsableBlockArea(node) && node.opened) as CollapsableBlockArea<C>[];
			for (const block of collapsableBlockAreas) {
				const r = await block.visitViewsAsync(visitor, options);
				if (r !== undefined) return r;
			}
		}
	}

	_blocksCtn: HTMLDivElement;
	_mainSlot: HTMLSlotElement;

	protected async _initialize(init: OBlocksCollapsableInit<C>): Promise<void> {
		//initConfig
		this._params = init;
		this._areas = init.areas;
		this._areasContext = init.areasContext;
		if (init.lastDatasKey) this.setAttribute('last-datas', init.lastDatasKey);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.findReg(init).installSkin('c-blocks-collapsable/base', sr);
		this._initAndInstallSkin(this.localName, init);

		this._blocksCtn = <div id="blocksCtn"/> as HTMLDivElement;
		/*new ArrowScrollBox().initialize({
			cbOverflow: undefined,//TODO
			vertical: true,
		});
		this._blocksCtn.addEventListener('focusout', (ev: FocusEvent) => {
			//sur la perte du focus on rescroll sur le block actif.
			if (this.selectedBlocks && ev.relatedTarget instanceof Node && !this._blocksCtn.contains(ev.relatedTarget)) {
				this._blocksCtn.scrollToChild(this.selectedBlocks[0]);
			}
		});
		 */
		this._mainSlot = <slot/> as HTMLSlotElement;

		this._initShadowRoot();

		this._initBlocks();
		if (init.lastDatas && init.lastDatas.open !== undefined) {
			if (init.lastDatas.open !== null) {
				Promise.all(init.lastDatas.open.map(async (code: string) => {
					const t = this.getBlockByCode(code);
					if (t) {
						await this.openBlock(t, init.lastDatas); //bon tab trouvé, on transmet les lastDatas.
					}
				}));
			}
		} else if (init.openBlocks != null) {
			Promise.all(init.openBlocks.map(async (code: string) => {
				const t = this.getBlockByCode(code);
				if (t) {
					await this.openBlock(t, init.lastDatas); //bon tab trouvé, on transmet les lastDatas.
				}
			}));
		}

		this._refreshUi();
	}

	buildInitFromAtts(init?: OBlocksCollapsableInit<C>): OBlocksCollapsableInit<C> {
		init = super.buildInitFromAtts(init);
		const areas = BASIS.extractAttr(this, 'areas-list');
		if (areas) init.areas = this.findReg(init).mergeLists<Area<C>>(...areas.split(' '));
		const areaCtx = BASIS.extractAttr(this, 'areas-context');
		if (areas && areaCtx == null) {
			//Une liste d'area est paramétrée en attribut, mais pas de contexte => contexte == le tabs.
			init.areasContext = this as any as C;
		} else if (areaCtx) {
			init.areasContext = this.findReg(init).getSvc<C>(areaCtx);
		}
		init.lastDatas = LASTDATAS.getLastDatas<JLDBlocksCollapsable>(this, this.getAttribute("last-datas"));
		return init;
	}

	_initShadowRoot() {
		const sr = this.shadowRoot;
		sr.appendChild(this._blocksCtn);
	}

	_initBlocks() {
		//Block en "dur"
		for (const view of this._mainSlot.assignedNodes()) {
			if (view instanceof CollapsableBlockArea) {
				this._initBlock(view);
				this._blocksCtn.appendChild(view);
			} else if (view instanceof HTMLElement) {
				const block = new CollapsableBlockArea<C>().initialize({
					parentBlockCtn: this,
					view: view
				} as OCollapsableBlockInit);
				this._initBlock(block);
				this.appendChild(<c-resizer c-orient="column"/>);
				this._blocksCtn.appendChild(block);
			}
		}
		//Onglets par Area.
		if (this._areas) this._areas.forEach((area: Area<C>) => {this._newBlock(area)});
	}

	protected _refreshUi() {
		// Recalcul de l'état des resizers en fonction de l'ouverture des blocks
		this._blocksCtn.childNodes.forEach((node) => {
			if (node instanceof Resizer) {
				let prev = node.previousElementSibling;
				let next = node.nextElementSibling;
				DOM.setAttrBool(node, "disabled", (prev && next && prev.hasAttribute("c-resizable") && next.hasAttribute("c-resizable")) ? false : true);
			}
		});
	}

	_newBlock(area: IArea<C>, before?: CollapsableBlockArea<C>, initBlock?: OCollapsableBlockInit): CollapsableBlockArea<C> {
		const block = new CollapsableBlockArea<C>().initialize(Object.assign({
			parentBlockCtn: this,
			area: area,
			areaContext: this.areasContext,
		} as OCollapsableBlockInit, initBlock));
		this._initBlock(block);
		this._blocksCtn.insertBefore(block, before);
		this._blocksCtn.insertBefore(<c-resizer c-orient="column"/>, block);
		return block;
	}

	_initBlock(block: CollapsableBlockArea<C>) {
	}

	/** appelé aprés tout changement d'état ouvert/clos d'un block */
	_onChangeBlockState(block: CollapsableBlockArea) {
		this._refreshUi();
	}

	buildLastDatas(p: JLastDatas) {
		const k = this.getAttribute("last-datas");
		if (k) {
			const d = p[k] = {} as JLDBlocksCollapsable;
			const blocks = this.selectedBlocks;
			if (blocks) {
				d.open = [];
				blocks.forEach((block) => {
					if (block.opened) d.open.push(block.code);
					LASTDATAS.buildLastDatas(d, block.view, true);
				});
			}
		}
	}

	protected _refresh() {
		let collapsableBlockAreas = Array.from(this._blocksCtn.childNodes).filter((node) => IS_CollapsableBlockArea(node) && node.opened) as CollapsableBlockArea<C>[];
		collapsableBlockAreas.map((entry) => entry.refresh());
	}
}

customElements.define('c-blocks-collapsable', BlocksCollapsable);

/** Stylage de base. */
REG.reg.registerSkin('c-blocks-collapsable/base', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
	  min-width: 0;
	  flex-direction: column;
  }

  c-arrow-scroll-box {
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex: 1;
  }

  #blocksCtn {
	  display: contents;
  }

  c-blocks-collapsable-block {
	  min-height: var(--blocks-collapsable-header-height, 2rem);
  }

  c-blocks-collapsable-block[aria-selected] {
	  min-height: calc(var(--blocks-collapsable-header-height, 2rem) + 5rem);
	}

	c-blocks-collapsable-block ~ c-blocks-collapsable-block {
		border-block-start: 4px solid transparent;
	}

`);


/**
 * Affichage d'une area das un block titré collapsable
 */
export interface OCollapsableBlockInit extends OSkinableInit, PickInit<CollapsableBlockArea<any>, 'view' | 'area' | 'areaContext' | 'parentBlockCtn'> {
	/** si true, le bloc est ouvert de façon forcée, sans capacité de close */
	locked?: boolean
}

function IS_CollapsableBlockArea(n: Node): n is CollapsableBlockArea {return n instanceof CollapsableBlockArea}

export class CollapsableBlockArea<C = {}> extends BaseAreaViewAsync<C> implements IViewContainer, IAreaPointer<C> {

	area: IArea<C, any>;

	areaContext: C;

	parentBlockCtn: BlocksCollapsable<C>

	view: IView;

	get code(): string {return VIEWS.getCode(this.view || this)}

	get label(): string {return VIEWS.getLabel(this.view || this)}

	get description(): string {return VIEWS.getDescription(this.view || this)}

	get icon(): string {return VIEWS.getIcon(this.view || this)}

	get disabled(): boolean {return !VIEWS.isEnabled(this.view || this)}

	get hidden(): boolean {return !VIEWS.isVisible(this.view || this)}

	get opened(): boolean {return this.hasAttribute("aria-selected")}

	get locked(): boolean {return this.hasAttribute("data-locked")}

	protected async _initialize(init: OCollapsableBlockInit) {
		//init config
		this.view = init.view;
		this.area = init.area;
		this.areaContext = init.areaContext;
		this.parentBlockCtn = init.parentBlockCtn ? init.parentBlockCtn : this.parentNode as BlocksCollapsable<C>;
		if (init.locked) DOM.setAttrBool(this, 'data-locked', true);
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		const sr = this.shadowRoot;
		this.initHeaderNode(sr.appendChild(<div class="header" ondblclick={this.onToggle}>
			<button class="toggle" onclick={this.onToggle}><span class="headTi">{(this as any as BaseAreaView<C>).label}</span></button>
		</div>));
		this.initContentNode(sr.appendChild(<div class="content">
			<slot/>
		</div>));

		if (init.locked)
			await this.show();
	}

	buildInitFromAtts(init?: OCollapsableBlockInit): OCollapsableBlockInit {
		init = super.buildInitFromAtts(init);
		return init;
	}

	protected _refreshUi() {
		if (this.description) DOM.setAttr(this, "title", this.description || this.label);
		DOM.setHidden(this, this.hidden);
		DOM.setAttrBool(this, "disabled", this.disabled);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}

	needAsync(): boolean {return this.area ? this.area.needAsync(this.areaContext) : false}

	/** Ne doit être appelé que par Blocks. */
	async closeBlockView(silently?: boolean): Promise<boolean> {
		if (this.view) {
			//la view a été construite.
			if (!await (silently ? VIEWS.canHideViewSilently(this.view, true) : VIEWS.canHideView(this.view, true, () => {this.parentBlockCtn.openBlock(this)}))) return false;
			VIEWS.onViewHidden(this.view, true);
			if (this.view) {
				this.view.remove();
				this.view = null;
			}
		}
		return true;
	}

	/** Ouverture du block */
	async show(lastDatas?: JLastDatas): Promise<void> {
		if (this.opened) return;
		DOM.setAttrBool(this, "aria-selected", true);
		DOM.setAttrBool(this, "c-resizable", true);
		this.parentBlockCtn._onChangeBlockState(this);
		this._refreshUi();
		if (!this.view) {
			if (this.needAsync()) {
				this.view = await this.area.loadBody(this.areaContext, lastDatas) as IView;
			} else {
				this.view = this.area.buildBody(this.areaContext, lastDatas) as IView;
			}
			if (this.view) {
				VIEWS.setArea(this.view, this);
				if (this.view instanceof BaseElementAsync) await this.view.initializedAsync;
				this.appendChild(this.view);
			}
			this._refreshUi();
		}
		if (this.opened)//RaceCond
			VIEWS.onViewShown(this.view);
	}

	/** Fermeture du block */
	async hide(): Promise<boolean> {
		if (!this.opened) return true; //déjà masqué.
		if (!await VIEWS.canHideView(this.view)) return false;
		DOM.setAttrBool(this, "aria-selected", false);
		DOM.setAttrBool(this, "c-resizable", false);
		this.parentBlockCtn._onChangeBlockState(this);
		this.removeAttribute("style")// raz effet resizer
		this._refreshUi();
		VIEWS.onViewHidden(this.view);
		return true;
	}

	protected initHeaderNode(node: Element) {
		return node;
	}

	protected initContentNode(node: Element) {
		return node;
	}

	private onToggle(this: HTMLElement, ev?: MouseEvent) {
		const me = DOMSH.findHost<CollapsableBlockArea<any>>(this);
		if (me.locked) return;
		if (me.opened)
			me.hide()
		else
			me.show()
	}
}

customElements.define('c-blocks-collapsable-block', CollapsableBlockArea);

REG.reg.registerSkin('c-blocks-collapsable-block', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	:host([hidden]) {
		display: none;
	}

  :host([disabled]){
		opacity: .6;
	  pointer-events: none;
  }

  :host([aria-selected]) {
	  flex: 1;
  }

  .header {
	  background-color: var(--tabf-bgcolor);
	  color: var(--tabf-color);
	  --pressed-bgcolor: var(--inv-pressed-bgcolor);
	  font-weight: bold;
	  line-height: var(--blocks-collapsable-header-height, 2rem);
  }

  .toggle {
	  background: none;
	  border: none;
	  color: var(--tabf-color);
	  user-select: none;
	  cursor: pointer;
	  font-size: inherit;
	}

	:host([data-locked]) .toggle {
	  pointer-events: none;
  }

  :host([data-locked]) .toggle::before {
	  visibility: hidden;
  }

  .toggle::before {
	  content: "▼ ";
  }

  :host(:not([aria-selected])) .toggle::before {
	  content: "▶ ";
  }

  .content {
	  flex: 1;
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  /*border-inline-start: 2px solid var(--tabf-bgcolor);*/
  }

  :host(:not([aria-selected])) .content {
	  display: none;
  }

`);